import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, Chip, Stack,
  Button, Alert, AlertTitle, Backdrop, CircularProgress,
  Divider, Rating
} from '@mui/material';
import {
  Folder, FolderOpen, LocalOffer, AutoAwesome,
  AccessTime, CreditCard, EditNote, PlayCircleOutline
} from '@mui/icons-material';

import { useTemplate } from '../contexts/TemplateContext';

// --- Premium Theme Config ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const darkGradient = 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)';
const lightBg = '#f4f6f8';

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

export default function TemplateDetails() {
  const { template_id } = useParams();

  // 🔴 Context se createDesignEntry bhi nikal liya
  const { fetchTemplateDetails, createDesignEntry } = useTemplate();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    const getDetails = async () => {
      const res = await fetchTemplateDetails(template_id);

      if (res.success) {
        setTemplate(res.data);
      } else {
        if (res.status === 401 && res.redirectUrl) {
          navigate(res.redirectUrl);
        } else {
          console.error("Error:", res.message);
        }
      }
    };

    getDetails();
  }, [template_id, fetchTemplateDetails, navigate]);

  // 🔴 API Call ke baad Redirect wala logic
  const handleCreateEntry = async () => {
    if (!template?.id) return alert("Error: Template ID missing.");

    setLoading(true);


    const res = await createDesignEntry(template_id);

    if (res.success) {
      setLoading(false);

      navigate(`/smart-form/${template_id}/create-entry`, {
        state: {
          entryData: res.data
        }
      });
    } else {
      setLoading(false);

      if (res.status === 401 && res.redirectUrl) {
        navigate(res.redirectUrl);
      } else {

        alert(res.message || "Failed to create entry");
      }
    }
  };

  if (!template) return null;

  const hasDiscount = template.original_price > 0 && template.sale_price < template.original_price;
  const discountPercent = hasDiscount
    ? Math.round(((template.original_price - template.sale_price) / template.original_price) * 100)
    : 0;
  const isFree = template.sale_price === 0 || (!template.sale_price && !template.original_price);

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pt: { xs: 2, md: 8 }, pb: { xs: 18, md: 15 }, boxSizing: 'border-box' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>

        <Card
          sx={{
            borderRadius: { xs: 0, sm: 3, md: 6 },
            overflow: 'hidden',
            boxShadow: { xs: 'none', sm: '0 10px 40px rgba(0,0,0,0.08)' },
            border: 'none',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            bgcolor: 'white',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {/* 🌟 LEFT: MEDIA PREVIEW 🌟 */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              background: darkGradient,
              position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              p: { xs: 0, md: 6 },
              minHeight: { xs: '300px', md: '70vh' },
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, opacity: 0.1, backgroundImage: 'linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />

            {template.sample_url ? (
              isVideoUrl(template.sample_url) ? (
                <Box component="video" controls poster={template.sample_thumbnail} sx={{ width: '100%', maxWidth: '100%', maxHeight: { xs: '350px', md: '65vh' }, objectFit: 'contain', zIndex: 1, boxShadow: { xs: 'none', md: '0 10px 30px rgba(0,0,0,0.3)' } }}>
                  <source src={template.sample_url} type="video/mp4" />
                </Box>
              ) : (
                <Box component="img" src={template.sample_url} alt={template.name} sx={{ width: '100%', maxWidth: '100%', maxHeight: { xs: '350px', md: '65vh' }, objectFit: 'contain', zIndex: 1, boxShadow: { xs: 'none', md: '0 10px 30px rgba(0,0,0,0.3)' } }} />
              )
            ) : (
              <Box textAlign="center" color="rgba(255,255,255,0.5)" zIndex={1}>
                <AutoAwesome sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">No Preview Available</Typography>
              </Box>
            )}
          </Box>

          {/* 🌟 RIGHT: TEMPLATE INFO 🌟 */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, p: { xs: 2.5, md: 6 }, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>

            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
              {template.designGroup && (
                <Chip size="small" icon={<Folder fontSize="small" />} label={template.designGroup.name} sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', fontWeight: 'bold', mb: 1 }} />
              )}
              {template.designSubGroup && (
                <Chip size="small" icon={<FolderOpen fontSize="small" />} label={template.designSubGroup.subgroup_name} sx={{ bgcolor: 'rgba(238, 9, 121, 0.1)', color: '#EE0979', fontWeight: 'bold', mb: 1 }} />
              )}
            </Stack>

            <Typography variant="h5" fontWeight="900" color="#1E293B" mb={1} sx={{
              lineHeight: 1.3,
              fontSize: { xs: '1.5rem', md: '2.5rem' },
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              width: '100%'
            }}>
              {template.name}
            </Typography>

            <Box display="flex" alignItems="center" mb={2.5}>
              <Rating value={template.rating || 5} precision={0.1} readOnly size="small" sx={{ color: '#f59e0b' }} />
              <Typography variant="body2" color="#64748B" ml={1} fontWeight="bold">({template.rating || 5.0})</Typography>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ bgcolor: '#f8fafc', p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3, border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
              {isFree ? (
                <Chip label="FREE TO USE" sx={{ background: 'linear-gradient(45deg, #10b981, #059669)', color: 'white', fontWeight: '900', fontSize: '1rem', p: 2, borderRadius: 2 }} />
              ) : (
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography variant="h3" fontWeight="900" color="#1E293B" mr={1.5} sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                    ₹{template.sale_price || template.original_price}
                  </Typography>

                  {hasDiscount && (
                    <Box display="flex" flexDirection="column" justifyContent="center">
                      <Typography variant="body1" color="#94A3B8" sx={{ textDecoration: 'line-through', fontWeight: '600', lineHeight: 1 }}>
                        ₹{template.original_price}
                      </Typography>
                      <Chip size="small" icon={<LocalOffer style={{ color: 'white' }} fontSize="small" />} label={`${discountPercent}% OFF`} sx={{ background: primaryGradient, color: 'white', fontWeight: 'bold', mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Stack spacing={1.5} mb={2} sx={{ width: '100%', boxSizing: 'border-box' }}>

              <Alert
                icon={<AccessTime fontSize="inherit" />}
                severity="info"
                sx={{
                  bgcolor: '#eff6ff', color: '#1e40af', border: 'none', borderRadius: 2, alignItems: 'flex-start',
                  width: '100%', boxSizing: 'border-box',
                  '& .MuiAlert-icon': { color: '#3b82f6', mt: 0.5 },
                  '& .MuiAlert-message': {
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden'
                  }
                }}
              >
                <AlertTitle sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.95rem' }}>Instant Generation</AlertTitle>
                <Typography variant="body2" sx={{
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: 'block'
                }}>
                  डिज़ाइन कुछ ही मिनटों में आपके विवरण के साथ तैयार हो जाएगा।
                </Typography>
              </Alert>

              {!isFree && (
                <Alert
                  icon={<CreditCard fontSize="inherit" />}
                  severity="success"
                  sx={{
                    bgcolor: '#ecfdf5', color: '#065f46', border: 'none', borderRadius: 2, alignItems: 'flex-start',
                    width: '100%', boxSizing: 'border-box',
                    '& .MuiAlert-icon': { color: '#10b981', mt: 0.5 },
                    '& .MuiAlert-message': {
                      width: '100%',
                      minWidth: 0,
                      overflow: 'hidden'
                    }
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.95rem' }}>Pay Later Priority</AlertTitle>
                  <Typography variant="body2" sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    display: 'block'
                  }}>
                    चिंता न करें! आप डिज़ाइन तैयार और चेक करने के बाद भुगतान कर सकते हैं।
                  </Typography>
                </Alert>
              )}
            </Stack>

          </Box>
        </Card>
      </Container>

      {/* 🌟 FLOATING ACTION BUTTON 🌟 */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 70, md: 30 },
          left: 0, right: 0,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          background: 'linear-gradient(to top, rgba(244,246,248,1) 40%, rgba(244,246,248,0) 100%)',
          pt: 4, pb: 1.5
        }}
      >
        <Button
          fullWidth
          onClick={handleCreateEntry}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <EditNote sx={{ fontSize: 24 }} />}
          sx={{
            maxWidth: '450px',
            background: primaryGradient, color: 'white',
            py: { xs: 1.6, md: 1.8 },
            borderRadius: '50px',
            fontSize: { xs: '1.05rem', md: '1.15rem' }, fontWeight: '900',
            boxShadow: '0 8px 25px rgba(238, 9, 121, 0.4)',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 30px rgba(238, 9, 121, 0.5)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#94a3b8' }
          }}
        >
          {loading ? "कृपया प्रतीक्षा करें..." : "अपना विवरण भरें (FILL DETAILS)"}
        </Button>
      </Box>

      {/* 🌟 LOADING BACKDROP 🌟 */}
      <Backdrop sx={{ color: '#fff', zIndex: 2000, flexDirection: 'column', bgcolor: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(5px)' }} open={loading}>
        <Box sx={{ bgcolor: 'white', p: 4, borderRadius: 4, textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', minWidth: 250 }}>
          <CircularProgress sx={{ color: '#FF6A00', mb: 2 }} size={50} thickness={4} />
          <Typography variant="h6" fontWeight="800" color="#1E293B">Please wait...</Typography>
        </Box>
      </Backdrop>

    </Box>
  );
}